/**
 * Error boundary — catches React render errors and shows
 * a safe fallback UI.
 */

import { Component, type ReactNode } from 'react';

interface State {
  hasError: boolean;
  message: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { hasError: false, message: '' };

  private resetForRoute = () => {
    if (this.state.hasError) this.setState({ hasError: false, message: '' });
  };

  override componentDidMount() {
    window.addEventListener('hashchange', this.resetForRoute);
  }

  override componentWillUnmount() {
    window.removeEventListener('hashchange', this.resetForRoute);
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, info: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  override render() {
    if (this.state.hasError) {
      const locale = document.documentElement.lang;
      const copy = locale.startsWith('ar')
        ? { title: 'حدث خطأ في هذه الشاشة', body: 'يمكنك العودة إلى لوحة اليوم أو المحاولة مرة أخرى.', retry: 'حاول مجددًا', home: 'العودة إلى لوحة اليوم' }
        : locale.startsWith('it')
          ? { title: 'Questa schermata ha riscontrato un errore', body: 'Puoi tornare alla panoramica o riprovare.', retry: 'Riprova', home: 'Torna alla panoramica' }
          : { title: 'This screen encountered an error', body: 'You can return to Today or try again.', retry: 'Try again', home: 'Return to Today' };
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="rounded-lg bg-red-50 p-6 text-center">
            <h2 className="text-lg font-semibold text-red-900">{copy.title}</h2>
            <p className="mt-2 text-sm text-red-700">{copy.body}</p>
            <button
              onClick={() => this.setState({ hasError: false, message: '' })}
              className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
            >
              {copy.retry}
            </button>
            <a href="#/app/dashboard" className="ml-3 inline-flex rounded border border-red-300 px-4 py-2 text-sm font-semibold text-red-900 hover:bg-red-100">
              {copy.home}
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
